import { useState } from "react";
import { Box, Link } from "@mui/material";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { Tab, useGetOne, useRecordContext } from "react-admin";
import { useNavigate } from "react-router-dom";
import { StyledShow } from "@/components/ui/react-admin/StyledShow";
import { StyledTabbedShowLayout } from "@/components/ui/react-admin/StyledTabbedShowLayout";
import { StyledShowWithRail, useRailCollapsed } from "@/components/ui/react-admin/StyledShowWithRail";
import { AuditLogBox } from "@/components/common/AuditLogBox";
import { SideDrawer } from "@/components/common/SideDrawer";
import { AttachmentPreview } from "@/components/common/DocumentsPanel";
import { DocumentRead, ResourceName, StoplossClaim } from "@/types";
import { composeDocumentLabel } from "@/utils/formatters/documentLabel";
import { RecomputeRefreshProvider } from "@/resources/claimants/hooks/useRecomputeRefresh";
import { claimLinesLabel, StoplossClaimProvider, useStoplossClaimContext } from "./hooks/useStoplossClaimContext";
import { StoplossClaimHeader } from "./components/StoplossClaimHeader";
import { StoplossClaimHoldBanner } from "./components/StoplossClaimHoldBanner";
import { StoplossClaimActionBar, StoplossClaimRail, StoplossClaimRailCards } from "./components/rail/StoplossClaimRail";
import { StoplossClaimItemsTab } from "./components/StoplossClaimItemsTab";
import { StoplossClaimReimbursementsTab } from "./components/StoplossClaimReimbursementsTab";
import { StoplossClaimDetailsTab } from "./components/details/StoplossClaimDetailsTab";
import { RepairClaimLinksDialog } from "./components/details/RepairClaimLinksDialog";
import { StoplossClaimSideDrawer } from "./components/StoplossClaimSideDrawer";

const TASKS_DRAWER_WIDTH = 500;
const PREVIEW_DRAWER_WIDTH = 640;

/**
 * Stop-loss claim page (Feature #7579). Content column (tabs, the medical or Rx
 * claims first) plus a sticky action rail holding the amount, the two tracks,
 * notes and the claim's documents; under 700 px the rail collapses to a sticky
 * bar and the notes and documents cards move under the tabs.
 * Design: docs/design/stoploss-claim-show-redesign (v6, in this repo).
 *
 * The page shares one deferred recompute refresh (RecomputeRefreshProvider) the
 * way the claimant page does: the hold strip that triggers it unmounts as soon
 * as the review resolves, so it cannot own the timers itself.
 */
export const StoplossClaimsShow = () => (
  <StyledShow actions={false}>
    <StoplossClaimShowBody />
  </StyledShow>
);

export const StoplossClaimShowBody = () => {
  const record = useRecordContext<StoplossClaim>();
  if (!record) return null;
  return (
    <StoplossClaimProvider claim={record}>
      <RecomputeRefreshProvider>
        <StoplossClaimPage />
      </RecomputeRefreshProvider>
    </StoplossClaimProvider>
  );
};

const TabLabel = ({ text, count }: { text: string; count?: number }) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.5 }}>
    {text}
    {count !== undefined ? (
      <Box component="span" sx={{ fontSize: "0.75rem", color: "text.secondary", fontWeight: 500 }}>
        {count}
      </Box>
    ) : null}
  </Box>
);

export const StoplossClaimPage = () => {
  const { claim, lineCounts, reimbursements } = useStoplossClaimContext();
  const navigate = useNavigate();
  const [tasksOpen, setTasksOpen] = useState(false);
  const [repairOpen, setRepairOpen] = useState(false);
  const railCollapsed = useRailCollapsed();

  // Document preview, hosted here like the claimant page. Selection is state
  // only; the document never lands in storage (ADR-086).
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const previewOpen = previewDocId !== null;
  const openDocument = (doc: DocumentRead | null) => setPreviewDocId(doc ? String(doc.id) : null);
  const { data: previewDoc } = useGetOne<DocumentRead>(
    ResourceName.DOCUMENTS,
    { id: previewDocId ?? "" },
    { enabled: !!previewDocId }
  );
  const previewTitle = previewDoc ? composeDocumentLabel(previewDoc) : "Document";

  const rightOffset = tasksOpen ? TASKS_DRAWER_WIDTH : previewOpen ? PREVIEW_DRAWER_WIDTH : 0;

  return (
    <>
      <Box sx={{ transition: "margin-right 0.3s ease", marginRight: `${rightOffset}px` }}>
        <StyledShowWithRail
          header={<StoplossClaimHeader onOpenTasks={() => setTasksOpen(true)} tasksOpen={tasksOpen} />}
          banner={railCollapsed ? <StoplossClaimHoldBanner /> : undefined}
          rail={<StoplossClaimRail onOpenDocument={(d) => openDocument(d)} onRepairLinks={() => setRepairOpen(true)} />}
          collapsedRail={<StoplossClaimActionBar onRepairLinks={() => setRepairOpen(true)} />}
          railWidth="clamp(360px, 28%, 480px)"
          railWidthNarrow={300}
        >
          <StyledTabbedShowLayout>
            <Tab label={<TabLabel text={claimLinesLabel(claim)} count={lineCounts.all} />}>
              <StoplossClaimItemsTab />
            </Tab>
            <Tab label={<TabLabel text="Reimbursements" count={reimbursements.length} />} path="reimbursements">
              <StoplossClaimReimbursementsTab />
            </Tab>
            <Tab label="Details" path="details">
              <StoplossClaimDetailsTab />
            </Tab>
            <Tab label="Audit log" path="audit">
              <AuditLogBox tableName={ResourceName.STOPLOSS_CLAIMS} />
            </Tab>
          </StyledTabbedShowLayout>
          {/* Below 700 px the rail is gone, so its notes and documents cards follow the tabs. */}
          {railCollapsed ? (
            <Box data-testid="rail-cards-collapsed" sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2 }}>
              <StoplossClaimRailCards onOpenDocument={(d) => openDocument(d)} />
            </Box>
          ) : null}
        </StyledShowWithRail>
      </Box>

      <StoplossClaimSideDrawer drawerExpanded={tasksOpen} toggleDrawer={() => setTasksOpen((v) => !v)} />

      <SideDrawer
        title={previewTitle}
        width={PREVIEW_DRAWER_WIDTH}
        open={previewOpen}
        onClose={() => setPreviewDocId(null)}
        hideHandle
      >
        {previewDocId && (
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box sx={{ pb: 1.5 }}>
              <Link
                component="button"
                onClick={() => void navigate(`/documents/${previewDocId}/show`)}
                underline="hover"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.25,
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Open the document page
                <ChevronRight sx={{ fontSize: 18 }} />
              </Link>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <AttachmentPreview documentId={previewDocId} />
            </Box>
          </Box>
        )}
      </SideDrawer>

      <RepairClaimLinksDialog open={repairOpen} onClose={() => setRepairOpen(false)} claim={claim} />
    </>
  );
};
